import React, { useCallback, useContext } from 'react';
import Markdown from 'markdown-to-jsx';
import {
  makeStyles,
  Divider,
  shorthands,
  tokens,
  mergeClasses,
  Button,
  Body1Strong,
  Body1,
} from '@fluentui/react-components';
import {
  Open20Regular,
  Link20Regular,
} from '@fluentui/react-icons';
import {
  FeedbackButtons,
  CopyButton,
  IFeedbackData,
  IAIMessage,
} from '@bingads-webui/ads-copilot-ui';
import { Message } from 'botframework-directlinejs';
import { type ILogger } from '@bingads-webui-react/msa-copilot-common';
import { useI18nContext } from '@bingads-webui/copilot-i18n';

import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { generateDateData, getDaysOfARange, parseCivilDate } from '@bingads-webui-universal/temporal-utitlities';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { IDateRange } from './types';
import { Fluctuation } from '../../types';

const useStyles = makeStyles({
  root: {
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
  },
  block: {
    display: 'block',
  },
  divider: {
    ...shorthands.margin(tokens.spacingVerticalMNudge, 0),
  },
  button: {
    marginTop: tokens.spacingVerticalS,
  },
  link: {
    width: '100%',
  },
  linkIcon: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  footer: {
    display: 'flex',
    marginTop: tokens.spacingVerticalL,
  },
  blocker: {
    minHeight: 'initial!important',

    '& .blocker-image': {
      width: '28px',
      height: '28px',
      ...shorthands.borderRadius(tokens.borderRadiusCircular),
      ...shorthands.border('2px', 'solid'),
      borderLeftColor: tokens.colorBrandForeground1,
      borderRightColor: tokens.colorBrandBackgroundInvertedHover,
      borderTopColor: tokens.colorBrandBackgroundInvertedHover,
      borderBottomColor: tokens.colorBrandBackgroundInvertedHover,
      marginRight: tokens.spacingHorizontalS,
      animationIterationCount: 'infinite',
      animationDuration: '1s',
      animationName: {
        to: { transform: 'rotate(360deg)' },
      },
    },
  },
});

type SummaryProps = {
  message: Message;
  fluctuation: Fluctuation,
  dateRange: IDateRange,
  dateFormat: string,
  logger: ILogger,
  replyToId: string,
  onFeedbackButtonClick: IAIMessage['onFeedbackButtonClick'];
};

const campaignTypeFilter = {
  Name: 'WebApp.CampaignUI.New.CampaignType',
  Data: {
    key: 0,
    value: {
      query: null,
    },
  },
};

const campaignStatusFilter = {
  Name: 'WebApp.CampaignUI.New.CampaignStatus',
  Data: {
    key: 1,
    value: {
      query: null,
    },
  },
};

export const Summary: React.FC<SummaryProps> = ({
  message,
  fluctuation,
  dateFormat,
  dateRange,
  logger,
  replyToId,
  onFeedbackButtonClick,
}) => {
  const styles = useStyles();
  const preferenceService = useContext(PreferenceContext) as any;
  const [savingPreference, setSavingPreference] = React.useState(false);
  const i18n = useI18nContext();

  const handleLogButtonClickChanges = () => logger?.trace(`[Insights Fluctuations Summary]: Button pressed: "View all changes". replyToId: ${replyToId}`);
  const handleLogButtonClickPerformance = () => logger?.trace(`[Insights Fluctuations Summary]: Button pressed: "Improve your ad campaign performance". replyToId: ${replyToId}`);
  const handleLogButtonClickOptimize = () => logger?.trace(`[Insights Fluctuations Summary]: Button pressed: "Optimize your ad campaigns with the help of Microsoft Advertising suggestions". replyToId: ${replyToId}`);
  const handleLogButtonClickROI = () => logger?.trace(`[Insights Fluctuations Summary]: Button pressed: "Increase your ROI with quality score and quality impact in depth". replyToId: ${replyToId}`);
  const handleLogButtonClickFeedback = (data) => logger?.trace(`[Insights Fluctuations Summary]: Button pressed: User pressed feedback on Fluctuation Summary. prevFeedback: ${data?.prevFeedback}, currentFeedback: ${data?.currentFeedback}. replyToId: ${replyToId}`);

  const onFeedbackButtonClickCb = useCallback((data: IFeedbackData) => {
    if (onFeedbackButtonClick) {
      handleLogButtonClickFeedback(data);
      onFeedbackButtonClick(data, message);
    }
  }, [message, onFeedbackButtonClick]);

  const { scope } = useContext(ScopeContext);
  const getChangeHistoryUrl = useCallback(() => {
    const { cid } = scope.values;

    const params = new URLSearchParams();
    params.append('cid', cid);
    params.append('aid', `${fluctuation.accountId}`);

    if (fluctuation.campaignId) {
      params.append('campaignId', `${fluctuation.campaignId}`);
    }

    return `${window.location.origin}/campaign/vnext/changehistory?${params.toString()}`;
  }, [fluctuation, scope]);

  const viewAllChanges = () => {
    setSavingPreference(true);
    const saveCampaignType = new Promise((resolve) => {
      preferenceService.setAtUserLevel(campaignTypeFilter).then(({ Id }: any) => {
        preferenceService.onRemoteSyncDone(Id).then(() => {
          resolve(Id);
        });
      });
    });

    const saveCampaignStatus = new Promise((resolve) => {
      preferenceService.setAtUserLevel(campaignStatusFilter).then(({ Id }: any) => {
        preferenceService.onRemoteSyncDone(Id).then(() => {
          resolve(Id);
        });
      });
    });

    let saveDateRange: Promise<any>;
    try {
      // parse the start/end dates from the fluctuation
      const selectedRange = {
        start: parseCivilDate({ i18n, dateString: fluctuation.shiftStartDate, dateFormat: 'yyyy-MM-dd' }),
        end: parseCivilDate({ i18n, dateString: fluctuation.shiftEndDate, dateFormat: 'yyyy-MM-dd' }),
      };

      // generate the new date filter preference value
      const dateRangePreferenceData = generateDateData(
        i18n,
        {
          selectedRange,
          rangeType: { rangeId: 4 },
          rangeIndex: 0,
          rangeDays: getDaysOfARange(selectedRange),
        },
        dateFormat,
        dateRange.Rangelcid,
      );

      // write the new date filter preference value
      saveDateRange = new Promise((resolve) => {
        preferenceService.setAtUserLevel({
          Data: dateRangePreferenceData,
          Name: 'WebApp.CampaignUI.dateFilter',
        })
          .then(({ Id }: any) => {
            preferenceService.onRemoteSyncDone(Id).then(() => {
              resolve(Id);
            });
          });
      });
    } catch (e) {
      logger?.trace(`[Insights Fluctuations Summary]: Failed to parse fluctuation dates: replyToId: ${replyToId}, shiftStartDate=${fluctuation.shiftStartDate}, shiftEndDate=${fluctuation.shiftEndDate}`);
      saveDateRange = Promise.resolve(true);
    }

    handleLogButtonClickChanges();

    Promise.all([saveCampaignType, saveCampaignStatus, saveDateRange]).finally(() => {
      setSavingPreference(false);
      window.open(getChangeHistoryUrl(), '_blank');
    });
  };

  return (
    <div className={styles.root}>
      <Body1Strong className={styles.block}>{i18n.getString(_TL_('Analysis'))}</Body1Strong>
      <Markdown
        className="content"
      >
        {message.text || ''}
      </Markdown>
      <Divider className={styles.divider} />
      <UiBlocker
        blocking={savingPreference}
        loadingText={i18n.getString(_TL_('Loading change history report'))}
        classes={{
          blockerIndicator: styles.blocker,
        }}
      >
        <Body1Strong className={styles.block}>{i18n.getString(_TL_('Change history'))}</Body1Strong>
        <Body1 className={styles.block}>{i18n.getString(_TL_('In addition to using our fluctuation analysis, reviewing your change history during this period can help you pinpoint the exact causes.'))}</Body1>
        <Button
          onClick={viewAllChanges}
          appearance="secondary"
          className={mergeClasses(styles.button, 'view-all-changes')}
          icon={<Open20Regular />}
          role="link"
        >
          {i18n.getString(_TL_('View all changes'))}
        </Button>
      </UiBlocker>
      <Divider className={styles.divider} />
      <Body1Strong className={styles.block}>{i18n.getString(_TL_('More ways to improve:'))}</Body1Strong>
      <Button
        as="a"
        appearance="secondary"
        className={mergeClasses('help-link', styles.link, styles.button)}
        icon={<Link20Regular />}
        role="link"
        target="_blank"
        href="https://go.microsoft.com/fwlink/?linkid=2283830"
        onClick={handleLogButtonClickPerformance}
      >
        {i18n.getString(_TL_('Improve your ad campaign performance.'))}
        <Open20Regular className={styles.linkIcon} />
      </Button>
      <Button
        as="a"
        appearance="secondary"
        className={mergeClasses('help-link', styles.link, styles.button)}
        icon={<Link20Regular />}
        role="link"
        target="_blank"
        href="https://go.microsoft.com/fwlink/?linkid=2283833"
        onClick={handleLogButtonClickOptimize}
      >
        {i18n.getString(_TL_('Optimize your ad campaigns with the help of Microsoft Advertising suggestions.'))}
        <Open20Regular className={styles.linkIcon} />
      </Button>
      <Button
        as="a"
        appearance="secondary"
        className={mergeClasses('help-link', styles.link, styles.button)}
        icon={<Link20Regular />}
        role="link"
        target="_blank"
        href="https://go.microsoft.com/fwlink/?linkid=2283834"
        onClick={handleLogButtonClickROI}
      >
        {i18n.getString(_TL_('Increase your ROI with quality score and quality impact in depth.'))}
        <Open20Regular className={styles.linkIcon} />
      </Button>
      <div className={styles.footer}>
        <FeedbackButtons onFeedbackChange={onFeedbackButtonClickCb} ariaHiddenWhileLoading={false} />
        <CopyButton messageText={message.text || ''} ariaHiddenWhileLoading={false} />
      </div>
    </div>
  );
};
